/**
 * Waits for ICE gathering, preferring a usable answer over a fast one.
 *
 * Signaling here is non-trickle: the offer is POSTed once, with its candidates
 * embedded, and the device never sends candidates back separately. So whatever
 * has been gathered when this resolves is the *entire* set of paths the
 * connection may ever use — anything still outstanding is lost.
 *
 * That makes a bare timer the wrong gate once TURN is involved. Host and
 * server-reflexive candidates appear almost immediately while a relay candidate
 * needs a full TURN allocation, so a short fixed timeout reliably produces an
 * offer containing only the candidates that cannot cross NAT — precisely the
 * case TURN was added to fix.
 *
 * Resolves as soon as gathering completes, or once at least one relay candidate
 * exists and the floor has elapsed, or at the ceiling regardless.
 */
export interface IceGatheringOptions {
  /** Give up and send whatever we have. */
  timeoutMs: number;
  /**
   * Minimum wait before an early exit on a relay candidate, so a fast
   * allocation does not cut off a second, better one.
   */
  minWaitMs?: number;
  /** Skip the relay requirement when no TURN server is configured. */
  expectRelay: boolean;
}

const POLL_INTERVAL_MS = 100;

export const waitForIceGathering = (
  pc: RTCPeerConnection,
  hasRelayCandidate: () => boolean,
  { timeoutMs, minWaitMs = 300, expectRelay }: IceGatheringOptions,
): Promise<void> =>
  new Promise<void>((resolve) => {
    const startedAt = Date.now();
    let settled = false;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(ceiling);
      resolve();
    };

    const ceiling = setTimeout(finish, timeoutMs);

    const check = () => {
      if (settled) {
        return;
      }

      if (pc.iceGatheringState === "complete") {
        finish();
        return;
      }

      const elapsed = Date.now() - startedAt;
      if (expectRelay && hasRelayCandidate() && elapsed >= minWaitMs) {
        finish();
        return;
      }

      setTimeout(check, POLL_INTERVAL_MS);
    };

    check();
  });

/** A candidate that routes through TURN — the only kind that survives symmetric NAT. */
export const isRelayCandidate = (candidate: RTCIceCandidate): boolean =>
  candidate.type === "relay" || / typ relay /.test(candidate.candidate);
